export type AnalyticsPrimitive = string | number | boolean | null;
export type AnalyticsValue =
  | AnalyticsPrimitive
  | AnalyticsPrimitive[]
  | AnalyticsProperties
  | AnalyticsProperties[];
export interface AnalyticsProperties {
  [key: string]: AnalyticsValue | undefined;
}

const sensitiveKeyPattern =
  /(address|allerg|authuid|birthday|body|caption|card|clientsecret|directdeposit|email|idempotency|note|password|payroll|phone|pin|reason|secret|ssn|taxform|token|url)/i;

const sensitiveIdentifierPattern =
  /^(appointment|client|conversation|giftcard|messageevent|packagebalance|paymentintent|payrollrun|reader|sale|setupintent|staff|toolcall)id$/i;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function isSafeMetadataKey(key: string) {
  return key.startsWith('has_') || key.endsWith('_enabled') || key.endsWith('_count');
}

export function isSensitiveAnalyticsKey(key: string) {
  if (isSafeMetadataKey(key)) {
    return false;
  }

  const normalizedKey = key.replace(/[_-]/g, '');

  return sensitiveKeyPattern.test(normalizedKey) || sensitiveIdentifierPattern.test(normalizedKey);
}

function sanitizeValue(value: unknown): AnalyticsValue | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .map((item) => sanitizeValue(item))
      .filter((item): item is AnalyticsValue => item !== undefined);

    return sanitizedItems as AnalyticsProperties[];
  }

  if (isPlainObject(value)) {
    return sanitizeAnalyticsProperties(value);
  }

  return undefined;
}

export function sanitizeAnalyticsProperties(properties: Record<string, unknown> = {}) {
  return Object.entries(properties).reduce<AnalyticsProperties>((safeProperties, [key, value]) => {
    if (isSensitiveAnalyticsKey(key)) {
      return safeProperties;
    }

    const sanitizedValue = sanitizeValue(value);

    if (sanitizedValue !== undefined) {
      safeProperties[key] = sanitizedValue;
    }

    return safeProperties;
  }, {});
}
